/**
 * ClaimModal — главный CTA по тапу на центральное ядро orbit.
 *
 * Согласно ТЗ Luna Protokol, юзер должен ежедневно нажимать CLAIM
 * чтобы забрать накопленную за 24ч прибыль и стартовать новый цикл.
 * Если генерация остановится (не нажал в срок) — генерация пауза
 * до следующего клейма.
 *
 * Если у юзера активирован Plasma Core — авто-клейм каждые 24ч,
 * генерация никогда не останавливается, юзер видит auto-claim
 * счётчик и заряд Plasma Core (раз в 7 дней нужна перезарядка).
 *
 * Эта модалка показывает оба сценария:
 *   - БЕЗ PC: накопленная сумма + кнопка CLAIM + countdown 24h
 *   - С PC : auto-claim ON + следующий drop + заряд Plasma Core
 */
import { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Zap, BatteryCharging } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { ClaimAnimation, type ClaimBreakdownEntry } from './ClaimAnimation';
import { lunaApi } from '@/services/api';
import { useUISounds } from '@/hooks/useUISounds';

const BUSINESS_SHORTS = ['TM', 'VPN', 'PF', 'ST', 'CS', 'AI', 'AD'] as const;

/** Split a total claim into 7 random per-business amounts that sum
 *  exactly to `total` (rounded to 2 decimals). Used for the success
 *  breakdown shown after the wow animation. */
function generateBreakdown(total: number): ClaimBreakdownEntry[] {
  const weights = BUSINESS_SHORTS.map(() => Math.random() + 0.15);
  const sum = weights.reduce((a, b) => a + b, 0);
  let allocated = 0;
  return BUSINESS_SHORTS.map((short, i) => {
    const isLast = i === BUSINESS_SHORTS.length - 1;
    const amount = isLast
      ? Math.round((total - allocated) * 100) / 100
      : Math.round(((total * weights[i]) / sum) * 100) / 100;
    allocated += amount;
    return { short, amount };
  });
}

interface ClaimModalProps {
  open: boolean;
  /** Сумма для РУЧНОГО клейма (только не-PC слоты) */
  pendingProfit: number;
  /** Seconds until manual claim ready (для не-PC слотов) */
  cycleSecondsLeft: number;
  /** Есть ли PC слоты у юзера */
  plasmaCoreActive: boolean;
  /** PC remaining charge 0-100 */
  plasmaCoreCharge?: number;
  /** Кол-во PC слотов (автоклейм) */
  pcContractsCount?: number;
  /** Кол-во ручных слотов */
  manualContractsCount?: number;
  /** Timestamp ближайшего PC автоклейма (ms) */
  pcNextClaimMs?: number | null;
  /** Сумма, накопившаяся на PC-слотах на текущий момент.
   *  Показывается рядом со строкой "авто" — чтобы юзер видел сколько
   *  ему прилетит автоматом, а не только таймер. */
  pcPendingTotal?: number;
  onClose: () => void;
  /**
   * Вызывается ПОСЛЕ успешной анимации клейма. Получает сумму
   * которую начислили — вызывающий может использовать её для
   * optimistic update локального баланса до ответа /me.
   */
  onClaim: (credited: number) => void;
}

/** Plural: 1 slot / 2-4 slots / 5+ slots. EN = one vs many. */
function pluralLang(n: number, one: string, few: string, many: string, lang: 'ru' | 'en'): string {
  if (lang === 'en') return n === 1 ? one : many;
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod100 >= 11 && mod100 <= 14) return many;
  if (mod10 === 1) return one;
  if (mod10 >= 2 && mod10 <= 4) return few;
  return many;
}

function formatHHMMSS(secs: number, t: TFunction): string {
  const h = Math.floor(secs / 3600);
  const m = Math.floor((secs % 3600) / 60);
  const s = secs % 60;
  return t('nodes.claimModal.timeHMS', {
    h, m: m.toString().padStart(2, '0'), s: s.toString().padStart(2, '0'),
  });
}

export function ClaimModal({
  open,
  pendingProfit,
  cycleSecondsLeft,
  plasmaCoreActive: _pcActive,
  plasmaCoreCharge = 100,
  pcContractsCount = 0,
  manualContractsCount = 0,
  pcNextClaimMs = null,
  pcPendingTotal = 0,
  onClose,
  onClaim,
}: ClaimModalProps) {
  const { t, i18n } = useTranslation();
  const lang: 'ru' | 'en' = i18n.language === 'ru' ? 'ru' : 'en';
  // 3 сценария:
  // 1) Все с PC (manual=0) — чистый авто-режим
  // 2) Все без PC (pc=0) — ручной claim
  // 3) Смешанный (оба > 0) — обе секции
  const mode: 'pc-only' | 'manual-only' | 'mixed' =
    pcContractsCount > 0 && manualContractsCount === 0 ? 'pc-only'
    : pcContractsCount === 0 ? 'manual-only'
    : 'mixed';

  const uiSounds = useUISounds();

  // Секунды до ближайшего PC автоклейма — тикает каждую секунду
  const [pcSecondsLeft, setPcSecondsLeft] = useState(() =>
    pcNextClaimMs ? Math.max(0, Math.round((pcNextClaimMs - Date.now()) / 1000)) : 0
  );
  useEffect(() => {
    if (!open || !pcNextClaimMs) return;
    setPcSecondsLeft(Math.max(0, Math.round((pcNextClaimMs - Date.now()) / 1000)));
    const id = window.setInterval(() => {
      setPcSecondsLeft(Math.max(0, Math.round((pcNextClaimMs - Date.now()) / 1000)));
    }, 1000);
    return () => clearInterval(id);
  }, [open, pcNextClaimMs]);
  // 'idle' = normal claim form, 'claiming' = wow success animation
  const [phase, setPhase] = useState<'idle' | 'claiming'>('idle');
  // Сумма, которая показывается в claiming-фазе. До ответа API
  // — это оптимистичный pendingProfit (то, что юзер видел на
  // дашборде). После ответа — реальный totalCredited из бэка.
  // ВАЖНО: state, а не ref, чтобы re-render обновлял <ClaimAnimation>
  // когда API ответит и значение поменяется.
  const [claimedAmount, setClaimedAmount] = useState(0);
  const breakdownRef = useRef<ClaimBreakdownEntry[]>([]);
  // Реф-накоп с момента прошлого клейма + доля ранг-буста.
  // Прилетает в ответе на /claim. Рисуем строчкой поверх модалки
  // в claiming-фазе чтобы юзер видел «партнёры принесли X, из них
  // Y — от ранга Lunar».
  const [refSummary, setRefSummary] = useState<{
    accumulated: number;
    boost: number;
    rankName: string | null;
  } | null>(null);

  // Reset to idle whenever the modal closes
  useEffect(() => {
    if (!open) {
      setPhase('idle');
    }
  }, [open]);

  // Escape closes (only in idle state — claiming animation must finish)
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && phase === 'idle') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose, phase]);

  const [claimError, setClaimError] = useState('');

  const startClaim = async () => {
    if (phase !== 'idle') return;
    if (pendingProfit < 0.001 || cycleSecondsLeft > 0) return;
    setClaimError('');
    // Оптимистично показываем pendingProfit (то что юзер видит
    // на дашборде). После ответа API заменим на реальный credited
    // из бэка — re-render обновит анимацию.
    setClaimedAmount(pendingProfit);
    breakdownRef.current = generateBreakdown(pendingProfit);
    setPhase('claiming');
    uiSounds.claimStart();
    try {
      const res = await lunaApi.claim.execute();
      const data = res.data as {
        success: boolean;
        totalCredited?: string;
        distributions?: Array<{ slotName: string; amountCredited: string }>;
        refAccumulatedSincePrev?: string;
        refBoostSincePrev?: string;
        rankNameAtClaim?: string | null;
      };
      const credited = parseFloat(data.totalCredited ?? String(pendingProfit));
      setClaimedAmount(credited);
      // Используем реальный breakdown если есть
      if (data.distributions && data.distributions.length > 0) {
        breakdownRef.current = data.distributions.map((d) => ({
          short: d.slotName.substring(0, 3).toUpperCase(),
          amount: parseFloat(d.amountCredited),
        }));
      } else {
        breakdownRef.current = generateBreakdown(credited);
      }
      // Реф + буст-сводка (для строки в модалке)
      const accumulated = parseFloat(data.refAccumulatedSincePrev ?? '0');
      const boost = parseFloat(data.refBoostSincePrev ?? '0');
      if (accumulated > 0) {
        setRefSummary({
          accumulated,
          boost,
          rankName: data.rankNameAtClaim ?? null,
        });
      } else {
        setRefSummary(null);
      }
    } catch (err: unknown) {
      const msg = (err as { message?: string })?.message || t('nodes.claimModal.claimError');
      setClaimError(msg);
      setClaimedAmount(pendingProfit);
      breakdownRef.current = generateBreakdown(pendingProfit);
    }
  };

  const handleAnimationComplete = () => {
    uiSounds.claimSuccess();
    onClaim(claimedAmount);
  };

  // В claiming-фазе используем claimedAmount (state, обновляется
  // после ответа API). До ответа = pendingProfit (оптимистично).
  // В idle-фазе — pendingProfit как раньше.
  const displayAmount =
    phase === 'claiming' ? claimedAmount : pendingProfit;

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-50 flex items-center justify-center px-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
          onClick={() => { uiSounds.bellClose(); onClose(); }}
        >
          <div className="absolute inset-0 bg-black/65 backdrop-blur-sm [.platform-non-ios_&]:bg-black/90 [.platform-non-ios_&]:backdrop-blur-none" />

          <motion.div
            className="relative w-full max-w-sm rounded-[28px]"
            style={{
              background:
                'linear-gradient(180deg, var(--glass-fill-strong) 0%, var(--glass-fill) 100%)',
              border: '1px solid var(--glass-stroke-strong)',
              boxShadow:
                'var(--glass-shadow), 0 0 80px -10px color-mix(in oklab, var(--accent-primary) 35%, transparent), inset 0 1.5px 0 0 var(--glass-rim-light), inset 0 -1px 0 0 var(--glass-rim-dark)',
              backdropFilter: 'none',
              WebkitBackdropFilter: 'none',
              // overflow: visible — чтобы success-rings и внешний halo
              // чекмарка могли расширяться за границы модалки без clip.
              overflow: 'visible',
              // В claiming-фазе отдаём ClaimAnimation гарантированную
              // высоту. Без этого absolute-позиционированные элементы
              // (Continue bottom:24, breakdown top:310+140) схлопывались
              // в нулевую высоту контейнера и визуально накладывались
              // друг на друга. 580px укладывает чек-анимацию + breakdown
              // + Continue-кнопку с нормальными зазорами.
              minHeight: phase === 'claiming' ? 700 : undefined,
            }}
            initial={{ y: 30, opacity: 0, scale: 0.94 }}
            animate={{ y: 0, opacity: 1, scale: 1 }}
            exit={{ y: 30, opacity: 0, scale: 0.94 }}
            transition={{ duration: 0.32, ease: [0.32, 0.72, 0, 1] }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Cinematic claim animation — replaces the form during claim */}
            {phase === 'claiming' && (
              <ClaimAnimation
                amount={displayAmount}
                breakdown={breakdownRef.current}
                hasPcContracts={pcContractsCount > 0}
                onComplete={handleAnimationComplete}
              />
            )}

            {/* Ранг-буст — показывается только если есть буст от ранга.
                Сумма "от партнёров" убрана, т.к. уже на балансе и
                дублирует историю в кошельке — юзеров путало. */}
            {phase === 'claiming' && refSummary && refSummary.boost > 0 && (
              <motion.div
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 1.8, ease: [0.32, 0.72, 0, 1] }}
                className="absolute bottom-[49px] left-5 right-5 z-30 rounded-2xl px-4 py-3"
                style={{
                  background: 'color-mix(in oklab, var(--accent-primary) 10%, var(--glass-fill-strong))',
                  border: '1px solid color-mix(in oklab, var(--accent-primary) 30%, var(--glass-stroke))',
                  boxShadow: '0 0 26px -10px color-mix(in oklab, var(--accent-primary) 45%, transparent)',
                }}
              >
                <p
                  className="text-[11.5px] leading-[1.45] text-center"
                  style={{ color: 'var(--accent-primary)' }}
                >
                  {t('claim.rankBoostLine1', {
                    amount: refSummary.boost.toLocaleString('ru-RU', { maximumFractionDigits: 3 }),
                  })}
                </p>
                <p
                  className="text-[11.5px] leading-[1.45] text-center"
                  style={{ color: 'var(--accent-primary)' }}
                >
                  {t('claim.rankBoostLine2', {
                    rank: refSummary.rankName ?? t('claim.rankFallback'),
                  })}
                </p>
              </motion.div>
            )}

            {/* Header — сумма к ручному клейму */}
            <div className="px-6 pt-6 pb-2 text-center" style={{ visibility: phase === 'claiming' ? 'hidden' : 'visible' }}>
              <p className="text-[10px] uppercase tracking-[0.22em] text-[var(--text-muted)] font-mono">
                {mode === 'pc-only' ? t('nodes.claimModal.autoActive') : t('nodes.claimModal.toClaim')}
              </p>
              <div className="flex items-baseline justify-center gap-1 mt-2">
                <span className="font-display text-[44px] font-extrabold text-[var(--text-primary)] tracking-tight tabular-nums">
                  {pendingProfit.toFixed(3)}
                </span>
                <span className="text-[14px] text-[var(--text-muted)] font-extrabold">USD₮</span>
              </div>
              {mode === 'mixed' && (
                <p className="text-[10px] text-[var(--text-muted)] mt-1">
                  {t('nodes.claimModal.mixedManualHint', {
                    count: manualContractsCount,
                    noun: pluralLang(manualContractsCount, t('nodes.claimModal.slotOne'), t('nodes.claimModal.slotMany'), t('nodes.claimModal.slotMany'), lang),
                  })}
                </p>
              )}
            </div>

            {/* ─── PC-ONLY: все слоты с авто-клеймом ─── */}
            {mode === 'pc-only' && (
              <div className="px-6 pt-3 pb-5" style={{ visibility: phase === 'claiming' ? 'hidden' : 'visible' }}>
                <div className="lg-pill !rounded-2xl px-4 py-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-[9px] uppercase tracking-[0.18em] text-[var(--text-muted)] font-mono">
                        {t('nodes.claimModal.nextAuto')}
                      </p>
                      <p className="font-mono text-[16px] font-extrabold mt-0.5 tabular-nums"
                         style={{ color: pcSecondsLeft === 0 ? 'var(--accent-primary)' : 'var(--text-primary)' }}>
                        {pcSecondsLeft === 0 ? t('nodes.claimModal.claimingNow') : formatHHMMSS(pcSecondsLeft, t)}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-[9px] uppercase tracking-[0.18em] text-[var(--text-muted)] font-mono">{t('nodes.claimModal.pcCharge')}</p>
                      <div className="flex items-center gap-1 mt-0.5">
                        <BatteryCharging size={14} className="text-[var(--accent-primary)]" />
                        <span className="font-mono text-[14px] font-extrabold text-[var(--accent-primary)] tabular-nums">
                          {plasmaCoreCharge}%
                        </span>
                      </div>
                    </div>
                  </div>
                  {/* Сумма к автосбору */}
                  {pcPendingTotal >= 0.001 && (
                    <div className="mt-2 pt-2 border-t border-[var(--glass-stroke)] flex items-center justify-between">
                      <span className="text-[9px] uppercase tracking-[0.18em] text-[var(--text-muted)] font-mono">
                        {t('nodes.claimModal.toReceiveAuto')}
                      </span>
                      <span className="font-display font-extrabold text-[14px] tabular-nums text-[var(--accent-primary)]">
                        +{pcPendingTotal.toFixed(3)} USD₮
                      </span>
                    </div>
                  )}
                </div>
                <div
                  className="mt-4 rounded-[var(--radius-pill)] py-3.5 text-center font-display text-[12px] font-bold tracking-[0.16em] uppercase text-[var(--accent-primary)]"
                  style={{
                    background: 'color-mix(in oklab, var(--accent-primary) 14%, transparent)',
                    border: '1px solid color-mix(in oklab, var(--accent-primary) 38%, transparent)',
                  }}
                >
                  {t('nodes.claimModal.pcAuto')}
                </div>
                <p className="text-[10px] text-[var(--text-muted)] text-center mt-3 leading-[1.5] whitespace-pre-line">
                  {t('nodes.claimModal.pcAutoNote')}
                </p>
              </div>
            )}

            {/* ─── MANUAL-ONLY: все слоты ручные ─── */}
            {mode === 'manual-only' && (
              <div className="px-6 pt-3 pb-5" style={{ visibility: phase === 'claiming' ? 'hidden' : 'visible' }}>
                <div className="lg-pill !rounded-2xl px-4 py-3 flex items-center justify-between">
                  <div>
                    <p className="text-[9px] uppercase tracking-[0.18em] text-[var(--text-muted)] font-mono">
                      {cycleSecondsLeft === 0 ? t('nodes.claimModal.readyToGet') : t('nodes.claimModal.nextCycleIn')}
                    </p>
                    <p className="font-mono text-[16px] font-extrabold mt-0.5 tabular-nums"
                       style={{ color: cycleSecondsLeft === 0 ? 'var(--accent-primary)' : 'var(--text-primary)' }}>
                      {cycleSecondsLeft === 0 ? t('nodes.claimModal.ready') : formatHHMMSS(cycleSecondsLeft, t)}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-[9px] uppercase tracking-[0.18em] text-[var(--text-muted)] font-mono">{t('nodes.claimModal.modeLabel')}</p>
                    <p className="text-[12px] font-mono font-extrabold text-[var(--text-secondary)] mt-0.5">{t('nodes.claimModal.manual')}</p>
                  </div>
                </div>

                <button
                  type="button"
                  onClick={startClaim}
                  disabled={pendingProfit < 0.001 || cycleSecondsLeft > 0}
                  className="mt-4 w-full rounded-[var(--radius-pill)] py-4 font-display text-[15px] font-extrabold tracking-[0.05em] uppercase text-[#0a0a0f] flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
                  style={{
                    background: 'linear-gradient(180deg, var(--accent-primary), color-mix(in oklab, var(--accent-primary) 65%, var(--accent-secondary)))',
                    boxShadow: '0 18px 36px -10px color-mix(in oklab, var(--accent-primary) 70%, transparent), inset 0 1px 0 0 rgba(255,255,255,0.55)',
                  }}
                >
                  <Zap size={16} strokeWidth={2.4} />
                  {pendingProfit < 0.001 ? t('nodes.claimModal.noProfitBtn')
                    : cycleSecondsLeft > 0 ? t('nodes.claimModal.waitingCycle')
                    : t('nodes.claimModal.claimBtn', { amount: pendingProfit.toFixed(3) })}
                </button>
                {claimError && <p className="text-[11px] text-[var(--status-negative)] text-center mt-2">{claimError}</p>}
                <p className="text-[10px] text-[var(--text-muted)] text-center mt-3 leading-[1.5]">
                  {t('nodes.claimModal.activatePCHint')}
                </p>
              </div>
            )}

            {/* ─── MIXED: PC часть автоматом, остальное руками ─── */}
            {mode === 'mixed' && (
              <div className="px-6 pt-3 pb-5 space-y-3" style={{ visibility: phase === 'claiming' ? 'hidden' : 'visible' }}>
                {/* PC блок — авто */}
                <div className="lg-pill !rounded-2xl px-4 py-3"
                  style={{
                    background: 'color-mix(in oklab, var(--accent-primary) 6%, transparent)',
                    border: '1px solid color-mix(in oklab, var(--accent-primary) 22%, transparent)',
                  }}>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-[9px] uppercase tracking-[0.18em] text-[var(--accent-primary)] font-mono">
                        {t('nodes.claimModal.mixedPCLabel', {
                          count: pcContractsCount,
                          noun: pluralLang(pcContractsCount, t('nodes.claimModal.slotCap1'), t('nodes.claimModal.slotCap2'), t('nodes.claimModal.slotCapMany'), lang),
                        })}
                      </p>
                      <p className="font-mono text-[13px] font-extrabold text-[var(--text-primary)] mt-0.5 tabular-nums">
                        {pcSecondsLeft === 0 ? t('nodes.claimModal.claimingNow') : t('nodes.claimModal.mixedThrough', { time: formatHHMMSS(pcSecondsLeft, t) })}
                      </p>
                    </div>
                    <div className="flex items-center gap-1">
                      <BatteryCharging size={14} className="text-[var(--accent-primary)]" />
                      <span className="font-mono text-[13px] font-extrabold text-[var(--accent-primary)] tabular-nums">
                        {plasmaCoreCharge}%
                      </span>
                    </div>
                  </div>
                  {/* Сумма, накопленная к PC-автосбору на текущий момент.
                      Показываем только если есть что отобразить (> 0.001),
                      чтобы не вводить в заблуждение нулевой строкой
                      сразу после предыдущего автосбора. */}
                  {pcPendingTotal >= 0.001 && (
                    <div className="mt-2 pt-2 border-t border-[var(--glass-stroke)] flex items-center justify-between">
                      <span className="text-[9px] uppercase tracking-[0.18em] text-[var(--text-muted)] font-mono">
                        {t('nodes.claimModal.toReceiveAuto')}
                      </span>
                      <span className="font-display font-extrabold text-[14px] tabular-nums text-[var(--accent-primary)]">
                        +{pcPendingTotal.toFixed(3)} USD₮
                      </span>
                    </div>
                  )}
                </div>

                {/* Manual блок — кнопка */}
                <div className="lg-pill !rounded-2xl px-4 py-3">
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <p className="text-[9px] uppercase tracking-[0.18em] text-[var(--text-muted)] font-mono">
                        {t('nodes.claimModal.mixedManualLabel', {
                          count: manualContractsCount,
                          noun: pluralLang(manualContractsCount, t('nodes.claimModal.slotCap1'), t('nodes.claimModal.slotCap2'), t('nodes.claimModal.slotCapMany'), lang),
                        })}
                      </p>
                      <p className="font-mono text-[13px] font-extrabold mt-0.5 tabular-nums"
                         style={{ color: cycleSecondsLeft === 0 ? 'var(--accent-primary)' : 'var(--text-primary)' }}>
                        {cycleSecondsLeft === 0 ? t('nodes.claimModal.readyToGet') : formatHHMMSS(cycleSecondsLeft, t)}
                      </p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={startClaim}
                    disabled={pendingProfit < 0.001 || cycleSecondsLeft > 0}
                    className="w-full rounded-[var(--radius-pill)] py-3 font-display text-[13px] font-extrabold tracking-[0.05em] uppercase text-[#0a0a0f] flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
                    style={{
                      background: 'linear-gradient(180deg, var(--accent-primary), color-mix(in oklab, var(--accent-primary) 65%, var(--accent-secondary)))',
                    }}
                  >
                    <Zap size={14} strokeWidth={2.4} />
                    {pendingProfit < 0.001 ? t('nodes.claimModal.noProfitShort')
                      : cycleSecondsLeft > 0 ? t('nodes.claimModal.waiting')
                      : t('nodes.claimModal.claimBtn', { amount: pendingProfit.toFixed(3) })}
                  </button>
                </div>
                {claimError && <p className="text-[11px] text-[var(--status-negative)] text-center">{claimError}</p>}
                <p className="text-[11px] text-[var(--text-muted)] text-center leading-[1.5] whitespace-pre-line">
                  {t('nodes.claimModal.installPCHint')}
                </p>
              </div>
            )}

            <div className="px-6 py-3 border-t border-[var(--glass-stroke)] text-[9px] uppercase tracking-[0.18em] text-[var(--text-disabled)] text-center font-mono" style={{ visibility: phase === 'claiming' ? 'hidden' : 'visible' }}>
              {t('nodes.claimModal.closeHint')}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
